// src/HistorialConsultas.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { obtenerMiHistorialIA } from "./api/historialIA";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./HistorialConsultas.css";
import { auth } from "./firebase";
import CarLoader from "./components/CarLoader";

function fmtTokens(n) {
    const v = Number(n || 0);
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
    return `${v}`;
}

function fmtDate(ts) {
    if (!ts) return "—";
    try {
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return d.toLocaleString("es-CL");
    } catch {
        return "—";
    }
}

export default function HistorialConsultas() {
    const [uid, setUid] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const [search, setSearch] = useState("");
    const [err, setErr] = useState("");

    // Thread UI: por historyId
    // { [historyId]: [{ role:"user"|"assistant", content:string, createdAt:number }] }
    const [threads, setThreads] = useState({});

    // Follow-up input
    const [followUp, setFollowUp] = useState("");
    const [followUpLoading, setFollowUpLoading] = useState(false);
    const [followUpErr, setFollowUpErr] = useState("");

    // Mantener thread en un ref para armar prompts sin problemas de setState async
    const threadsRef = useRef({});
    useEffect(() => {
        threadsRef.current = threads;
    }, [threads]);

    // callable
    const functions = getFunctions();
    const generarRespuesta = httpsCallable(functions, "generarRespuestaConTool");

    // Auth
    useEffect(() => onAuthStateChanged(auth, (u) => setUid(u?.uid ?? null)), []);

    // Cargar historial — sin listener: se refresca al montar y tras cada
    // follow-up (que es lo único que lo cambia). Ver plan de migración, Fase 5.
    const cargarHistorial = async () => {
        if (!uid) {
            setItems([]);
            setSelected(null);
            setErr("");
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const rows = await obtenerMiHistorialIA("gemini");
            setItems(rows);
            setErr("");

            setSelected((prev) => {
                if (prev && rows.some((r) => r.id === prev.id)) {
                    return rows.find((r) => r.id === prev.id) || prev;
                }
                return rows[0] || null;
            });
        } catch (e) {
            console.error("Historial query error:", e);
            setErr(e?.message || "Error cargando historial");
            setItems([]);
            setSelected(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { cargarHistorial(); }, [uid]);

    // filtro búsqueda
    const filteredItems = useMemo(() => {
        const s = search.trim().toLowerCase();
        if (!s) return items;
        return items.filter((it) => {
            const p = String(it.prompt || "").toLowerCase();
            const r = String(it.response || "").toLowerCase();
            return p.includes(s) || r.includes(s);
        });
    }, [items, search]);

    // Centrado vertical solo si hay pocos
    const leftCentered = filteredItems.length > 0 && filteredItems.length <= 3;

    // Al cambiar selección, limpiar input/error (pero NO borrar hilo)
    useEffect(() => {
        setFollowUp("");
        setFollowUpErr("");
        setFollowUpLoading(false);
    }, [selected?.id]);

    const selectedTokens = (selected?.inputTokens ?? 0) + (selected?.outputTokens ?? 0);

    const currentThread = selected?.id ? threads[selected.id] || [] : [];

    async function handleFollowUpSubmit(e) {
        e.preventDefault();
        const question = followUp.trim();
        if (!question) return;

        if (!uid) {
            setFollowUpErr("Debes estar logueado para preguntar.");
            return;
        }
        if (!selected?.id) {
            setFollowUpErr("Selecciona una consulta del historial primero.");
            return;
        }

        const historyId = selected.id;

        // 1) Agregar pregunta al hilo (optimista)
        setThreads((prev) => {
            const cur = prev[historyId] || [];
            const next = [
                ...cur,
                { role: "user", content: question, createdAt: Date.now() },
            ];
            return { ...prev, [historyId]: next };
        });

        setFollowUpLoading(true);
        setFollowUpErr("");
        setFollowUp(""); // ✅ queda listo para la siguiente

        // 2) Construir prompt con contexto: original + hilo
        const baseThread = threadsRef.current[historyId] || [];
        const promptThread = [
            ...baseThread,
            { role: "user", content: question, createdAt: Date.now() },
        ];

        const threadText = promptThread
            .map((m) => (m.role === "user" ? `Usuario: ${m.content}` : `Asistente: ${m.content}`))
            .join("\n\n");

        const prompt = `
Eres un asistente experto en análisis de precios de vehículos.
Usa el contexto histórico y el hilo para responder.

[CONSULTA ORIGINAL]
${selected.prompt || ""}

[RESPUESTA ORIGINAL]
${selected.response || ""}

[HILO]
${threadText}

Responde de forma clara y concreta. Si falta información, indícalo y sugiere el dato faltante.
`.trim();

        try {
            const res = await generarRespuesta({
                uid,
                prompt,
                origen: "historial_thread",
                contexto: { historyId },
            });

            const data = res?.data;
            const answer =
                (typeof data === "string" && data) ||
                data?.response ||
                data?.text ||
                JSON.stringify(data, null, 2);

            // 3) Agregar respuesta al hilo
            setThreads((prev) => {
                const cur = prev[historyId] || [];
                const next = [
                    ...cur,
                    { role: "assistant", content: answer, createdAt: Date.now() },
                ];
                return { ...prev, [historyId]: next };
            });

            // El follow-up creó un exchange nuevo en el historial — refrescar
            // la lista (ya no hay listener que lo haga solo).
            cargarHistorial();
        } catch (error) {
            console.error("Follow-up thread error:", error);
            setFollowUpErr(error?.message || "Error al consultar la IA.");
        } finally {
            setFollowUpLoading(false);
        }
    }

    return (
        <main className="main-content">
            <div className="history-page">
                <div className="history-card">
                    <div className="history-topbar">
                        <div>
                            <div className="history-title">Historial de consultas</div>
                            <div className="history-subtitle">Tus últimas preguntas y respuestas</div>
                        </div>
                    </div>

                    {!uid ? (
                        <div className="history-empty">Debes estar logueado para ver el historial.</div>
                    ) : (
                        <div className="history-body">
                            {/* LEFT */}
                            <aside className="history-left">
                                <div className="history-search">
                                    <input
                                        className="history-search-input"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Buscar en preguntas y respuestas…"
                                    />
                                </div>

                                <div className={`history-list ${leftCentered ? "is-centered" : ""}`}>
                                    {err ? <div className="history-error">{err}</div> : null}

                                    {loading ? (
                                        <div className="history-empty"><CarLoader label="Cargando historial…" /></div>
                                    ) : filteredItems.map((it) => {
                                        const tokens = (it?.inputTokens ?? 0) + (it?.outputTokens ?? 0);
                                        return (
                                            <button
                                                key={it.id}
                                                type="button"
                                                className={`history-item ${selected?.id === it.id ? "active" : ""}`}
                                                onClick={() => setSelected(it)}
                                                title="Ver detalle"
                                            >
                                                <div className="history-item-top">
                                                    <span className="history-item-date">{fmtDate(it.createdAt)}</span>
                                                    <span className="history-item-tokens">{fmtTokens(tokens)}</span>
                                                </div>
                                                <div className="history-item-prompt">
                                                    {String(it.prompt || "").slice(0, 90)}
                                                    {String(it.prompt || "").length > 90 ? "…" : ""}
                                                </div>
                                            </button>
                                        );
                                    })}

                                    {!loading && !err && filteredItems.length === 0 ? (
                                        <div className="history-empty">
                                            {search.trim()
                                                ? `No hay resultados para “${search}”.`
                                                : "Aún no tienes consultas registradas."}
                                        </div>
                                    ) : null}
                                </div>
                            </aside>

                            {/* RIGHT */}
                            <section className="history-right">
                                {!selected ? (
                                    <div className="history-empty">Selecciona una consulta de la izquierda.</div>
                                ) : (
                                    <>
                                        <div className="history-meta">
                                            <div>
                                                <span className="k">Fecha</span>
                                                <span className="v">{fmtDate(selected.createdAt)}</span>
                                            </div>
                                            <div>
                                                <span className="k">Tokens</span>
                                                <span className="v">{fmtTokens(selectedTokens)}</span>
                                            </div>
                                            <div>
                                                <span className="k">Modelo</span>
                                                <span className="v">{selected.model || "—"}</span>
                                            </div>
                                            <div>
                                                <span className="k">Latencia</span>
                                                <span className="v">{selected.latencyMs ?? "—"} ms</span>
                                            </div>
                                        </div>

                                        <div className="history-block">
                                            <div className="history-block-title">Pregunta</div>
                                            <div className="history-block-body">{selected.prompt}</div>
                                        </div>

                                        <div className="history-block">
                                            <div className="history-block-title">Respuesta</div>
                                            <div className="history-block-body markdown">
                                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                    {selected.response || ""}
                                                </ReactMarkdown>
                                            </div>
                                        </div>

                                        {/* THREAD */}
                                        <div className="history-thread">
                                            <div className="history-thread-title">Hilo</div>

                                            {currentThread.length === 0 ? (
                                                <div className="history-empty" style={{ padding: 10 }}>
                                                    Aún no hay preguntas adicionales para esta consulta.
                                                </div>
                                            ) : (
                                                <div className="history-thread-messages">
                                                    {currentThread.map((m, idx) => (
                                                        <div key={idx} className={`history-thread-msg ${m.role}`}>
                                                            <div className="history-thread-role">
                                                                {m.role === "user" ? "Tú" : "IA"}
                                                            </div>
                                                            <div className="history-thread-content markdown">
                                                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                    {m.content}
                                                                </ReactMarkdown>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* FOLLOW-UP INPUT */}
                                            <form className="history-followup-form" onSubmit={handleFollowUpSubmit}>
                                                <input
                                                    className="history-followup-input"
                                                    value={followUp}
                                                    onChange={(e) => setFollowUp(e.target.value)}
                                                    placeholder="Haz una pregunta adicional sobre esta consulta…"
                                                    disabled={followUpLoading}
                                                />

                                                <button
                                                    type="submit"
                                                    className="history-followup-btn"
                                                    disabled={!followUp.trim() || followUpLoading}
                                                    title="Preguntar usando el contexto"
                                                >
                                                    {followUpLoading ? "Consultando…" : "Preguntar"}
                                                </button>
                                            </form>

                                            {followUpErr ? <div className="history-error">{followUpErr}</div> : null}
                                        </div>
                                    </>
                                )}
                            </section>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}